import type { MapEngine } from "../MapEngine";
import type { StyleJson } from "../types";
import { getXrayJson } from "../style";

/**
 * Toggles the map between its normal style and an x-ray debug style
 * (per-source-layer raw geometry, via {@link getXrayJson}). The manager owns
 * the "current applied normal style" so it can swap back when x-ray is turned
 * off. Written once; attached to a MapEngine.
 */
export class XrayManager {
    private readonly engine: MapEngine;
    private normalStyle: StyleJson | null = null;
    private active = false;

    constructor(engine: MapEngine) {
        this.engine = engine;
    }

    /**
     * Records the normal style the viewer just applied. Call this on every
     * Update so x-ray derives from the latest style. Re-applies x-ray if it is
     * currently on.
     */
    setNormalStyle(style: StyleJson): void {
        this.normalStyle = style;
        if (this.active) {
            this.engine.setStyle(getXrayJson(style));
        }
    }

    /** Whether x-ray is currently showing. */
    isActive(): boolean {
        return this.active;
    }

    /** Turns x-ray on/off, swapping the style accordingly. No-op without a style. */
    setActive(active: boolean): void {
        if (active === this.active || !this.normalStyle) {
            this.active = active && !!this.normalStyle;
            return;
        }
        this.active = active;
        this.engine.setStyle(active ? getXrayJson(this.normalStyle) : this.normalStyle);
    }

    /** Convenience toggle; returns the new state. */
    toggle(): boolean {
        this.setActive(!this.active);
        return this.active;
    }
}
